import { useEffect, useState, useRef, useMemo } from 'react';
import queryString from 'query-string';
import { useLocation, useNavigate } from 'react-router-dom';
import { trackFilterChange } from '../mixpanel';
import { useMapFilter } from '../hooks/useMapFilter';
import { validateRedirectPath } from '../helper/urlValidation';

const useFilters = (initialFilters) => {
	const location = useLocation();
	const navigate = useNavigate();
	const isInitialLoad = useRef(true);
	const previousFilters = useRef(initialFilters);

	// Parse URL params immediately
	const initialUrlParams = useMemo(() => {
		const params = queryString.parse(location.search, {
			arrayFormat: 'bracket-separator',
			arrayFormatSeparator: '|',
		});
		// Convert page to number if it exists
		if (params.page) {
			params.page = parseInt(params.page, 10);
		}
		return params;
	}, [location.search]);

	// Initialize filters with URL params merged with initial filters
	const [filters, setFilters] = useState(() => {
		const mergedFilters = {
			...initialFilters,
			...initialUrlParams,
		};
		// Ensure page is a number
		if (mergedFilters.page) {
			mergedFilters.page = parseInt(mergedFilters.page, 10);
		}
		return mergedFilters;
	});

	const mappedFilters = useMapFilter(filters);

	const cleanFilters = (filters) => {
		const newFilters = Object.fromEntries(
			Object.entries(filters || {}).filter(
				([_, value]) =>
					value !== undefined &&
					value !== '' &&
					!(Array.isArray(value) && value.length === 0)
			)
		);
		return newFilters;
	};

	const hasFiltersChanged = (prev, current) => {
		if (!prev || !current) return false;
		
		// Compare all keys except page
		const prevKeys = Object.keys(prev).filter(key => key !== 'page');
		const currentKeys = Object.keys(current).filter(key => key !== 'page');
		
		// Check if number of filter keys changed
		if (prevKeys.length !== currentKeys.length) return true;
		
		// Check if any filter values changed
		return currentKeys.some((key) => {
			const prevValue = prev[key];
			const currentValue = current[key];
			
			// Handle array comparison
			if (Array.isArray(prevValue) && Array.isArray(currentValue)) {
				return JSON.stringify(prevValue) !== JSON.stringify(currentValue);
			}
			
			return prevValue !== currentValue;
		});
	};

	useEffect(() => {
		const cleanedFilters = cleanFilters(filters);

		// Only track and update page if filters have changed (not on initial load)
		if (!isInitialLoad.current && hasFiltersChanged(previousFilters.current, cleanedFilters)) {
			cleanedFilters.page = 1;
			trackFilterChange(mappedFilters);
			setFilters((prev) => ({ ...prev, page: 1 }));
		}

		// Update URL if filters have changed
		const params = queryString.stringify(cleanedFilters, {
			arrayFormat: 'bracket-separator',
			arrayFormatSeparator: '|',
		});

		const prevParams = queryString.stringify(previousFilters.current, {
			arrayFormat: 'bracket-separator',
			arrayFormatSeparator: '|',
		});

		if (params !== prevParams) {
			// Validate pathname before using in navigation
			const safePath = validateRedirectPath(location.pathname) ? location.pathname : '/';
			navigate(`${safePath}?${params}`, { replace: true });
			previousFilters.current = cleanedFilters;
		}

		// Mark initial load as complete after first render
		if (isInitialLoad.current) {
			isInitialLoad.current = false;
			previousFilters.current = cleanedFilters;
		}
	}, [filters, location.pathname, mappedFilters, navigate]);

	return [filters, setFilters];
};

export default useFilters;
